import _pick from 'lodash/pick';
import _get from 'lodash/get';
import {
  GeojsonFeatureCollectionInputObject,
  SiteCreateInputObject,
  SiteUpdateInputObject,
} from 'graphql-common';
import { Option, Values } from '@lib/interfaces/form';
import getPhoneNumberPreparedDataFromValues from '@lib/utils/getPhoneNumberPreparedDataFromValues';
import { SelectOption } from '@lib/components/Select/types';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import { prepareAttachedField } from '@lib/utils/prepareAttachedField';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import removePropertiesTypeDeep from '@lib/utils/removePropertiesTypeDeep';
import { addWhiteToIconUrl } from 'routes/Sites/utils/addWhiteToIconUrl';

export default function getSitePreparedDataFromValues(
  values: Values,
  prevValues: Values,
) {
  const newValues = _pick(values, [
    'code',
    'description',
    'email',
    'industry',
    'name',
  ]);
  const phoneNumber = getPhoneNumberPreparedDataFromValues(
    values,
    SiteFieldNames.PhoneNumber,
  );
  const conductCodeSections = _get(values, SiteFieldNames.ConductCodeSections);
  let newConductCodeSections;
  if (conductCodeSections && Array.isArray(conductCodeSections)) {
    newConductCodeSections = conductCodeSections.length
      ? conductCodeSections.map((item, index) => ({
          title: _get(item, SiteFieldNames.Title),
          body: _get(item, SiteFieldNames.Body),
          position: index + 1,
        }))
      : [];
  }

  const areas = _get(values, SiteFieldNames.Areas);
  let newAreas;
  if (areas && Array.isArray(areas)) {
    // Map nested area documentationLinks from Option[] to backend shape.
    // Site wizard only creates new areas (no unlink needed), so this is a
    // plain mapping; unlink diffs for existing areas are handled via the
    // dedicated SiteArea modal.
    newAreas = areas.length
      ? areas.map((area: Values) => {
          const docLinks = _get(area, SiteFieldNames.DocumentationLinks) as
            Array<Option & { visibleViaQr?: boolean }> | undefined;
          if (docLinks === undefined) return area;
          return {
            ...area,
            [SiteFieldNames.DocumentationLinks]: docLinks.length
              ? docLinks.map(({ value, visibleViaQr }) =>
                  visibleViaQr === undefined
                    ? { id: `${value}` }
                    : { id: `${value}`, visibleViaQr },
                )
              : undefined,
          };
        })
      : undefined;
  }

  const locationPhotoAttached = prepareAttachedField(
    _get(values, SiteFieldNames.LocationPhotoAttached),
  );

  const sectorId = _get(values, [SiteFieldNames.Sector, 'value']);

  const assigneesValue = (_get(values, SiteFieldNames.UserMemberships) ||
    []) as SelectOption[];
  const prevAssigneesValue = (_get(
    prevValues,
    SiteFieldNames.UserMemberships,
  ) || []) as SelectOption[];
  const members = assigneesValue.map(({ value }) => ({
    id: `${value}`,
  }));
  const prevMembers = prevAssigneesValue.map(({ value }) => ({
    id: `${value}`,
  }));
  const userMemberships = mergeAndUnlinkMultiSelectValues(prevMembers, members);

  const address = _get(values, SiteFieldNames.Address, null) as string;
  const locationPoint = _get(
    values,
    SiteFieldNames.LocationPoint,
  ) as GeojsonFeatureCollectionInputObject;
  const pointerShape = _get(values, [SiteFieldNames.PointerShape, 'value']);
  const pointerColor = _get(values, [SiteFieldNames.PointerColor, 'value']);
  const pointerIcon = _get(values, [
    SiteFieldNames.PointerIconAttached,
    'fileTag',
  ]);

  if (locationPoint && Array.isArray(locationPoint.features)) {
    for (let i = 0; i < locationPoint.features.length; i += 1) {
      const feature = locationPoint.features[i];
      if (feature) {
        const props = feature.properties || {};
        props.addressDetails = address;
        if (pointerShape) {
          props.shape = pointerShape;
        }
        if (pointerColor) {
          // Store the backend `ColorEnum` value as-is.
          props.color = pointerColor;
        }
        if (pointerIcon) {
          props.icon = addWhiteToIconUrl(pointerIcon);
        }
        feature.properties = props;
      }
    }
  }

  return {
    ...newValues,
    areas: newAreas,
    userId: _get(values, 'userId.value'),
    conductCodeSections: newConductCodeSections,
    locationPhotoAttached,
    phoneNumber,
    sectorId: sectorId || null,
    userMemberships,
    locationPoint: locationPoint
      ? {
          geojson: removePropertiesTypeDeep(removeTypenameDeep(locationPoint)),
        }
      : undefined,
  } as SiteCreateInputObject | SiteUpdateInputObject;
}
